import { cn } from 'shared-ui';

import { ClientSwitcherDropdown, type NetworkExplorerSwitcherEntry } from './ClientSwitcherDropdown';

export interface AppHeaderProps {
    /** Active module prefix, already uppercased & branded (e.g. "FIND & FIX®"). Omitted when no module is active. */
    moduleLabel?: string;
    sectionLabel?: string;
    clientName?: string;
    clientLogoSrc?: string | null;
    /** The Network Explorer entry of the switcher — see ClientSwitcherDropdown. */
    explorer?: NetworkExplorerSwitcherEntry;
    /** Fired after a client row is chosen in the switcher — see ClientSwitcherDropdown. */
    onClientSelected?: (clientId: string) => void;
    className?: string;
}

export function AppHeader({
    moduleLabel,
    sectionLabel = 'Network Overview',
    clientName = 'Northern Ireland Water',
    clientLogoSrc,
    explorer,
    onClientSelected,
    className,
}: AppHeaderProps) {
    const title = moduleLabel ? `${moduleLabel} - ${sectionLabel}` : sectionLabel;
    return (
        <header
            className={cn(
                'bg-background order-2 flex shrink-0 items-center border-t px-4',
                'py-2 md:order-1 md:h-20 md:border-t-0! md:border-b md:py-0',
                className,
            )}
        >
            {/*
             * Single flex row — children are reordered between breakpoints via CSS order.
             * Mobile  (<md): logo left (order-1), text right (order-2), gap-3 between.
             * Desktop (md+): text left (md:order-1), logo right (md:order-2), justify-between.
             * Using order avoids display:none / display:flex conflicts in Tailwind v4.
             */}
            <div className="flex w-full items-center gap-3 md:justify-between">
                <div className="order-2 flex min-w-0 flex-col md:order-1">
                    <span className="text-origin-blue text-xs leading-4 font-medium">{title}</span>
                    <span className="truncate text-lg leading-7 font-black uppercase md:text-3xl md:leading-9">
                        {clientName}
                    </span>
                </div>
                <div className="order-1 shrink-0 md:order-2">
                    <ClientSwitcherDropdown
                        clientName={clientName}
                        clientLogoSrc={clientLogoSrc}
                        explorer={explorer}
                        onClientSelected={onClientSelected}
                    />
                </div>
            </div>
        </header>
    );
}
